// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Shield, Loader2, Plus, Pencil, Trash2, Check, X, CalendarCheck, DollarSign,
} from "lucide-react";
import { formatTime, formatTimeRange } from "@/lib/dates";

const FN = "rle-manage-officials";

async function callOfficials(body: any) {
  const { data, error } = await invokeEdgeFunction(FN, { body });
  if (error) throw new Error(error.message || "Request failed");
  if (data && data.ok === false) {
    throw new Error(data.error?.message || data.error || "Request failed");
  }
  return data?.data ?? {};
}

const dollars = (cents?: number | null) =>
  `$${(((cents ?? 0) as number) / 100).toFixed(2)}`;
const toCents = (v: any) =>
  v === "" || v == null ? null : Math.round(Number(v) * 100);
const fmtDate = (d?: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString() : "—");

const STATUS_VARIANT: Record<string, any> = {
  accepted: "default", declined: "destructive", assigned: "secondary",
};

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </CardContent>
  </Card>
);

/* ---------------- Official dialog ---------------- */
const emptyOfficial = {
  name: "", email: "", phone: "", certification_level: "", default_game_fee: "",
};

const OfficialDialog = ({ open, onOpenChange, initial, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyOfficial);
  React.useEffect(() => {
    if (open) {
      setForm({
        ...emptyOfficial,
        ...(initial || {}),
        default_game_fee:
          initial?.default_game_fee_cents != null
            ? (initial.default_game_fee_cents / 100).toString()
            : "",
      });
    }
  }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit official" : "Add official"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email ?? ""} onChange={set("email")} /></div>
          <div><Label>Phone</Label><Input value={form.phone ?? ""} onChange={set("phone")} /></div>
          <div><Label>Certification level</Label><Input value={form.certification_level ?? ""} onChange={set("certification_level")} placeholder="Level 1, IAABO…" /></div>
          <div><Label>Default game fee ($)</Label><Input type="number" step="0.01" value={form.default_game_fee} onChange={set("default_game_fee")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={busy || !form.name.trim()}
            onClick={() =>
              onSave({
                name: form.name.trim(),
                email: form.email || null,
                phone: form.phone || null,
                certification_level: form.certification_level || null,
                default_game_fee_cents: toCents(form.default_game_fee),
              })
            }
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Availability dialog ---------------- */
const emptyAvail = {
  official_id: "", available_date: "", start_time: "", end_time: "", is_available: true, notes: "",
};

const AvailabilityDialog = ({ open, onOpenChange, initial, officials, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyAvail);
  React.useEffect(() => { if (open) setForm({ ...emptyAvail, ...(initial || {}) }); }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit availability" : "Add availability"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Official</Label>
            <Select value={form.official_id} onValueChange={(v) => setForm((f: any) => ({ ...f, official_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select official" /></SelectTrigger>
              <SelectContent>
                {(officials ?? []).map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.available_date || ""} onChange={set("available_date")} /></div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={!!form.is_available} onCheckedChange={(v) => setForm((f: any) => ({ ...f, is_available: v }))} />
            <span className="text-sm">{form.is_available ? "Available" : "Unavailable"}</span>
          </div>
          <div><Label>Start time</Label><Input type="time" value={form.start_time || ""} onChange={set("start_time")} /></div>
          <div><Label>End time</Label><Input type="time" value={form.end_time || ""} onChange={set("end_time")} /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes ?? ""} onChange={set("notes")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={busy || !form.official_id || !form.available_date}
            onClick={() => onSave({
              official_id: form.official_id,
              available_date: form.available_date,
              start_time: form.start_time || null,
              end_time: form.end_time || null,
              is_available: !!form.is_available,
              notes: form.notes || null,
            })}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Assign dialog ---------------- */
const AssignDialog = ({ open, onOpenChange, game, officials, onSave, busy }: any) => {
  const [officialId, setOfficialId] = useState("");
  const [position, setPosition] = useState("");
  const [fee, setFee] = useState("");
  React.useEffect(() => { if (open) { setOfficialId(""); setPosition(""); setFee(""); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign official</DialogTitle>
        </DialogHeader>
        {game && (
          <p className="text-sm text-muted-foreground">
            {game.team_a_name} vs {game.team_b_name}
            {game.scheduled_date ? ` · ${fmtDate(game.scheduled_date)}` : ""}
          </p>
        )}
        <div className="grid gap-3">
          <div>
            <Label>Official</Label>
            <Select value={officialId} onValueChange={setOfficialId}>
              <SelectTrigger><SelectValue placeholder="Select official" /></SelectTrigger>
              <SelectContent>
                {(officials ?? []).map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Position (optional)</Label><Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Referee, Umpire…" /></div>
          <div>
            <Label>Game fee ($, optional)</Label>
            <Input type="number" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Leave blank for default" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={busy || !officialId}
            onClick={() => onSave({ official_id: officialId, position: position || null, game_fee_cents: toCents(fee) })}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Page ---------------- */
const OperatorEventOfficialsHubPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const orgId = profile?.organization_id ?? null;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const [officialDialog, setOfficialDialog] = useState<{ open: boolean; initial?: any }>({ open: false });
  const [availDialog, setAvailDialog] = useState<{ open: boolean; initial?: any }>({ open: false });
  const [assignDialog, setAssignDialog] = useState<{ open: boolean; game?: any }>({ open: false });

  const summaryQ = useQuery({
    queryKey: ["officials-summary", eventId],
    enabled: !!eventId,
    queryFn: () => callOfficials({ action: "summary", event_id: eventId }),
  });
  const rosterQ = useQuery({
    queryKey: ["officials-roster", orgId],
    enabled: !!orgId,
    queryFn: () => callOfficials({ action: "roster_list", organization_id: orgId }),
  });
  const assignmentsQ = useQuery({
    queryKey: ["officials-assignments", eventId],
    enabled: !!eventId,
    queryFn: () => callOfficials({ action: "event_assignments", event_id: eventId }),
  });
  const availQ = useQuery({
    queryKey: ["officials-availability", eventId],
    enabled: !!eventId,
    queryFn: () => callOfficials({ action: "availability_list", event_id: eventId }),
  });
  const payQ = useQuery({
    queryKey: ["officials-pay", eventId],
    enabled: !!eventId,
    queryFn: () => callOfficials({ action: "pay_summary", event_id: eventId }),
  });

  const officials = rosterQ.data?.officials ?? [];
  const games = assignmentsQ.data?.games ?? [];
  const availability = availQ.data?.availability ?? [];
  const pay = payQ.data?.pay ?? [];
  const summary = summaryQ.data ?? {};

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["officials-summary", eventId] });
    qc.invalidateQueries({ queryKey: ["officials-assignments", eventId] });
    qc.invalidateQueries({ queryKey: ["officials-pay", eventId] });
  };

  const run = async (fn: () => Promise<any>, successMsg: string, after: () => void) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      after();
    } catch (e: any) {
      toast.error(e.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Shield className="h-6 w-6 text-secondary" /> Officials
        </h1>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="roster">Roster</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
          <TabsTrigger value="pay">Pay</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-6">
          {summaryQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Tile label="Officials Assigned" value={String(summary.officials_assigned ?? 0)} />
              <Tile label="Assignments" value={String(summary.assignments ?? 0)} />
              <Tile label="Accepted" value={String(summary.accepted ?? 0)} />
              <Tile label="Total Fees" value={dollars(summary.total_fee_cents)} />
              <Tile label="Availability Slots" value={String(summary.availability_slots ?? 0)} />
            </div>
          )}
        </TabsContent>

        {/* Roster */}
        <TabsContent value="roster" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Official roster</CardTitle>
              <Button size="sm" onClick={() => setOfficialDialog({ open: true })}>
                <Plus className="mr-1 h-4 w-4" /> Add official
              </Button>
            </CardHeader>
            <CardContent>
              {rosterQ.isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : officials.length === 0 ? (
                <p className="text-sm text-muted-foreground">No officials yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Certification</TableHead>
                      <TableHead>Default fee</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead className="w-24" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {officials.map((o: any) => (
                      <TableRow key={o.id}>
                        <TableCell className="font-medium">{o.name}</TableCell>
                        <TableCell>{o.email || "—"}</TableCell>
                        <TableCell>{o.phone || "—"}</TableCell>
                        <TableCell>{o.certification_level || "—"}</TableCell>
                        <TableCell>{o.default_game_fee_cents != null ? dollars(o.default_game_fee_cents) : "—"}</TableCell>
                        <TableCell>
                          {o.user_id
                            ? <Badge variant="default">Linked</Badge>
                            : <Badge variant="outline">No account</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => setOfficialDialog({ open: true, initial: o })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => {
                              if (!confirm(`Delete ${o.name}?`)) return;
                              run(
                                () => callOfficials({ action: "official_delete", id: o.id }),
                                "Official deleted",
                                () => { qc.invalidateQueries({ queryKey: ["officials-roster", orgId] }); refreshAll(); },
                              );
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments */}
        <TabsContent value="assignments" className="mt-6 space-y-3">
          {assignmentsQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : games.length === 0 ? (
            <p className="text-sm text-muted-foreground">No scheduled games for this event yet.</p>
          ) : (
            games.map((g: any) => (
              <Card key={g.id}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">{g.team_a_name} vs {g.team_b_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {fmtDate(g.scheduled_date)}
                        {g.scheduled_start_time ? ` · ${formatTime(g.scheduled_start_time)}` : ""}
                        {g.court_name ? ` · ${g.court_name}` : ""}
                        {g.division ? ` · ${g.division}` : ""}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setAssignDialog({ open: true, game: g })}>
                      <Plus className="mr-1 h-4 w-4" /> Assign official
                    </Button>
                  </div>

                  {g.officials.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {g.officials.map((a: any) => (
                        <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
                          <span className="text-sm font-medium">{a.official_name}</span>
                          {a.position && <span className="text-xs text-muted-foreground">{a.position}</span>}
                          <Badge variant={STATUS_VARIANT[a.status] ?? "secondary"}>{a.status}</Badge>
                          <span className="text-xs text-muted-foreground">{dollars(a.game_fee_cents)}</span>
                          <div className="ml-auto flex items-center gap-1">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => run(
                                () => callOfficials({ action: "assignment_status", id: a.id, status: "accepted" }),
                                "Marked accepted", refreshAll,
                              )}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => run(
                                () => callOfficials({ action: "assignment_status", id: a.id, status: "declined" }),
                                "Marked declined", refreshAll,
                              )}
                            >
                              <X className="h-4 w-4 text-amber-600" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => run(
                                () => callOfficials({ action: "unassign_game", id: a.id }),
                                "Assignment removed", refreshAll,
                              )}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Availability */}
        <TabsContent value="availability" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarCheck className="h-4 w-4" /> Availability
              </CardTitle>
              <Button size="sm" onClick={() => setAvailDialog({ open: true })}>
                <Plus className="mr-1 h-4 w-4" /> Add window
              </Button>
            </CardHeader>
            <CardContent>
              {availQ.isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : availability.length === 0 ? (
                <p className="text-sm text-muted-foreground">No availability recorded.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Official</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Window</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availability.map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">{a.official?.name ?? "—"}</TableCell>
                        <TableCell>{fmtDate(a.available_date)}</TableCell>
                        <TableCell>
                          {a.start_time ? formatTimeRange(a.start_time, a.end_time) : "All day"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={a.is_available ? "default" : "outline"}>
                            {a.is_available ? "Available" : "Unavailable"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.notes || "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => setAvailDialog({ open: true, initial: { ...a, official_id: a.official_id } })}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pay */}
        <TabsContent value="pay" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4" /> Payout sheet
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payQ.isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : pay.length === 0 ? (
                <p className="text-sm text-muted-foreground">No assignments to pay out yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Official</TableHead>
                      <TableHead>Games</TableHead>
                      <TableHead>Accepted</TableHead>
                      <TableHead className="text-right">Total fee</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pay.map((p: any) => (
                      <TableRow key={p.official_id}>
                        <TableCell className="font-medium">{p.official_name}</TableCell>
                        <TableCell>{p.games}</TableCell>
                        <TableCell>{p.accepted}</TableCell>
                        <TableCell className="text-right">{dollars(p.total_fee_cents)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold" colSpan={3}>Total</TableCell>
                      <TableCell className="text-right font-semibold">
                        {dollars(pay.reduce((s: number, p: any) => s + (p.total_fee_cents ?? 0), 0))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <OfficialDialog
        open={officialDialog.open}
        initial={officialDialog.initial}
        busy={busy}
        onOpenChange={(o: boolean) => setOfficialDialog({ open: o })}
        onSave={(official: any) =>
          run(
            () => callOfficials({
              action: "official_save",
              organization_id: orgId,
              official,
              id: officialDialog.initial?.id,
            }),
            "Official saved",
            () => {
              setOfficialDialog({ open: false });
              qc.invalidateQueries({ queryKey: ["officials-roster", orgId] });
            },
          )
        }
      />

      <AvailabilityDialog
        open={availDialog.open}
        initial={availDialog.initial}
        officials={officials}
        busy={busy}
        onOpenChange={(o: boolean) => setAvailDialog({ open: o })}
        onSave={(availability: any) =>
          run(
            () => callOfficials({
              action: "availability_save",
              event_id: eventId,
              availability,
              id: availDialog.initial?.id,
            }),
            "Availability saved",
            () => {
              setAvailDialog({ open: false });
              qc.invalidateQueries({ queryKey: ["officials-availability", eventId] });
              qc.invalidateQueries({ queryKey: ["officials-summary", eventId] });
            },
          )
        }
      />

      <AssignDialog
        open={assignDialog.open}
        game={assignDialog.game}
        officials={officials}
        busy={busy}
        onOpenChange={(o: boolean) => setAssignDialog({ open: o })}
        onSave={(payload: any) =>
          run(
            () => callOfficials({ action: "assign_game", game_id: assignDialog.game.id, ...payload }),
            "Official assigned",
            () => { setAssignDialog({ open: false }); refreshAll(); },
          )
        }
      />
    </div>
  );
};

export default OperatorEventOfficialsHubPage;
